import { CalendarClock, ShieldAlert, Inbox } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { Team, Vitals } from '@/lib/teams';
import type { Ticket } from '@/lib/mock';
import type { Widget } from '@/lib/dashboards';
import { heatOf } from '@/lib/mock';
import { thermalEdge } from '@/lib/temp';
import { Sparkline } from '@/components/hub/Sparkline';
import { untilLabel } from '@/lib/useNow';
import { cn } from '@/lib/utils';

export type WidgetProps = {
  team: Team;
  vitals: Vitals;
  queue: Ticket[];
  now: number;
  onOpen: (id: string) => void;
};

export function WidgetCard({ widget, children }: { widget: Widget; children: React.ReactNode }) {
  const span = widget.span ?? 1;
  return (
    <section
      className={cn(
        'rounded-xl border border-border bg-card p-4 shadow-sm',
        span === 3 && 'sm:col-span-2 lg:col-span-3',
        span === 2 && 'sm:col-span-2 lg:col-span-2',
      )}
    >
      <h2 className="mb-3 text-[13px] font-semibold tracking-tight">{widget.title}</h2>
      {children}
    </section>
  );
}

function Empty({ label = 'No tickets yet' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-6 text-center text-sm text-muted-foreground">
      <Inbox className="h-5 w-5 opacity-60" />
      {label}
    </div>
  );
}

// priority → severity / tier helpers (real mapping, not mock)
const SEV: Record<string, string> = { P1: 'Critical', P2: 'High', P3: 'Medium', P4: 'Low' };
const TIER: Record<string, string> = { P1: 'L3', P2: 'L2', P3: 'L1', P4: 'L1' };

function countBy<T>(items: T[], key: (t: T) => string): [string, number][] {
  const m = new Map<string, number>();
  for (const it of items) m.set(key(it), (m.get(key(it)) ?? 0) + 1);
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

// ---- widgets ------------------------------------------------------------
function VitalsWidget({ vitals, team }: WidgetProps) {
  const ident = `hsl(${team.hue} 65% 52%)`;
  const tiles = [
    { label: 'Open', value: vitals.open },
    { label: 'Breaching', value: vitals.breaching, warn: vitals.breaching > 0 },
    { label: 'Unassigned', value: vitals.unassigned },
    { label: 'Avg age', value: `${vitals.avgResH}h` },
    { label: 'Capacity', value: `${Math.round(vitals.capacity * 100)}%` },
    { label: 'Members', value: team.members.length },
  ];
  return (
    <div className="flex items-center gap-6">
      <div className="grid flex-1 grid-cols-3 gap-4 sm:grid-cols-6">
        {tiles.map((t) => (
          <div key={t.label}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t.label}</div>
            <div className={cn('font-mono text-xl font-medium', t.warn && 'text-temp-hot')}>{t.value}</div>
          </div>
        ))}
      </div>
      <div className="hidden h-10 w-40 sm:block">
        <Sparkline data={vitals.spark} color={ident} className="h-full w-full" />
      </div>
    </div>
  );
}

function QueueWidget({ queue, now, onOpen }: WidgetProps) {
  if (queue.length === 0) return <Empty label="No open tickets for this team" />;
  return (
    <div className="-mx-1 max-h-[22rem] space-y-1 overflow-y-auto">
      {queue.map((t) => (
        <button
          key={t.id}
          onClick={() => onOpen(t.id)}
          className="relative flex w-full items-center gap-3 rounded-lg px-3 py-2 pl-4 text-left transition hover:bg-muted/50"
        >
          <span className="absolute inset-y-2 left-0 w-[3px] rounded-full" style={{ background: thermalEdge(heatOf(t, now)) }} />
          <span className="font-mono text-[11px] text-muted-foreground">{t.ref}</span>
          <span className="flex-1 truncate text-sm">{t.subject}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{t.state}</span>
          <span className="font-mono text-[11px] text-muted-foreground">{untilLabel(t.slaDueAt, now)}</span>
        </button>
      ))}
    </div>
  );
}

function CategoryWidget({ queue, team }: WidgetProps) {
  const rows = countBy(queue, (t) => t.category);
  if (rows.length === 0) return <Empty />;
  const max = Math.max(...rows.map((r) => r[1]));
  const ident = `hsl(${team.hue} 65% 52%)`;
  return (
    <div className="space-y-2.5">
      {rows.slice(0, 6).map(([label, v]) => (
        <div key={label}>
          <div className="mb-1 flex justify-between text-xs">
            <span>{label}</span>
            <span className="font-mono text-muted-foreground">{v}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: ident }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SlaWidget({ queue, now }: WidgetProps) {
  if (queue.length === 0) return <Empty />;
  const breached = queue.filter((t) => now > t.slaDueAt).length;
  const atRisk = queue.filter((t) => heatOf(t, now) >= 0.65 && now <= t.slaDueAt).length;
  const onTrack = queue.length - breached - atRisk;
  const pct = (n: number) => Math.round((n / queue.length) * 100);
  const rows = [
    { label: 'On track', n: onTrack, tone: 'text-emerald-500' },
    { label: 'At risk', n: atRisk, tone: 'text-temp-warm' },
    { label: 'Breached', n: breached, tone: 'text-temp-ember' },
  ];
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{r.label}</span>
          <span className={cn('font-mono font-medium', r.tone)}>
            {r.n} · {pct(r.n)}%
          </span>
        </div>
      ))}
    </div>
  );
}

function EscalationWidget({ queue }: WidgetProps) {
  const tiers = ['L1', 'L2', 'L3'].map((t) => [t, queue.filter((q) => TIER[q.priority] === t).length] as const);
  const max = Math.max(...tiers.map((t) => t[1]), 1);
  return (
    <div className="flex items-end gap-2">
      {tiers.map(([t, n]) => (
        <div key={t} className="flex flex-1 flex-col items-center gap-1">
          <div className="flex w-full items-end justify-center rounded-lg bg-muted" style={{ height: 24 + (n / max) * 44 }}>
            <span className="pb-1 font-mono text-sm font-medium">{n}</span>
          </div>
          <span className="font-mono text-[11px] text-muted-foreground">{t}</span>
        </div>
      ))}
    </div>
  );
}

function ChangeCalendarWidget({ queue, now }: WidgetProps) {
  const changes = queue.filter((t) => t.module === 'Change').sort((a, b) => a.slaDueAt - b.slaDueAt);
  if (changes.length === 0) return <Empty label="No scheduled changes" />;
  return (
    <ul className="space-y-2 text-sm">
      {changes.map((c) => (
        <li key={c.id} className="flex items-center gap-2">
          <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-mono text-[11px] text-muted-foreground">{untilLabel(c.slaDueAt, now)}</span>
          <span className="truncate">{c.subject}</span>
        </li>
      ))}
    </ul>
  );
}

function SeverityMatrixWidget({ queue }: WidgetProps) {
  const order = ['Critical', 'High', 'Medium', 'Low'];
  const tone = ['var(--temp-ember)', 'var(--temp-hot)', 'var(--temp-warm)', 'var(--temp-cool)'];
  const counts = order.map((s) => queue.filter((t) => SEV[t.priority] === s).length);
  return (
    <div className="grid grid-cols-4 gap-2">
      {order.map((s, i) => (
        <div key={s} className="rounded-xl border border-border p-3 text-center">
          <div className="font-mono text-2xl font-medium" style={{ color: `hsl(${tone[i]})` }}>{counts[i]}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">{s}</div>
        </div>
      ))}
    </div>
  );
}

function MttrWidget({ vitals, queue, now }: WidgetProps) {
  const newest = queue.length ? Math.min(...queue.map((t) => (now - t.createdAt) / 60000)) : 0;
  return (
    <div className="space-y-3">
      <Stat label="Newest open" value={`${Math.round(newest)}m`} sub="time since latest report" />
      <Stat label="Avg age (open)" value={`${vitals.avgResH}h`} sub="mean age of open tickets" />
    </div>
  );
}

function ThreatsWidget({ queue, onOpen }: WidgetProps) {
  const threats = queue.filter((t) => t.priority === 'P1' || t.priority === 'P2').slice(0, 6);
  if (threats.length === 0) return <Empty label="No active threats" />;
  return (
    <ul className="space-y-2">
      {threats.map((t) => (
        <li key={t.id}>
          <button onClick={() => onOpen(t.id)} className="flex w-full items-center gap-2 text-left text-sm hover:text-primary">
            <ShieldAlert className={cn('h-4 w-4', t.priority === 'P1' ? 'text-temp-ember' : 'text-temp-hot')} />
            <span className="flex-1 truncate">{t.subject}</span>
            <span className="font-mono text-[11px] text-muted-foreground">{t.ci ?? t.priority}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function StateBoard({ queue, cols }: { queue: Ticket[]; cols: string[] }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0,1fr))` }}>
      {cols.map((c) => {
        const n = queue.filter((t) => t.state.toLowerCase().includes(c.toLowerCase())).length;
        return (
          <div key={c} className="rounded-xl border border-border p-3">
            <div className="text-[11px] text-muted-foreground">{c}</div>
            <div className="mt-1 font-mono text-lg font-medium">{n}</div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-xl border border-[rgba(255,255,255,0.05)] bg-[rgba(0,0,0,0.1)] p-4 shadow-inner">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted">{label}</span>
        <span className="font-mono text-2xl font-bold text-theme">{value}</span>
      </div>
      <div className="mt-1 text-[11px] font-medium text-muted/70">{sub}</div>
    </div>
  );
}

function StatusDistributionWidget({ queue }: WidgetProps) {
  const statuses = ['Open', 'Assigned', 'In Progress', 'Pending', 'On Hold', 'Resolved', 'Closed', 'Cancelled'];
  const data = statuses.map(s => ({
    name: s,
    count: queue.filter(t => t.state?.toLowerCase() === s.toLowerCase() || (s === 'Open' && t.state?.toLowerCase() === 'new')).length
  }));
  
  if (queue.length === 0) return <Empty />;

  return (
    <div className="h-64 w-full pt-4">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 10, fill: 'var(--color-text)', opacity: 0.7 }}
            axisLine={false}
            tickLine={false}
            interval={0}
            angle={-25}
            textAnchor="end"
          />
          <YAxis 
            tick={{ fontSize: 10, fill: 'var(--color-text)', opacity: 0.7 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip 
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            contentStyle={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-neo-border)', borderRadius: '12px', color: 'var(--color-text)', boxShadow: 'var(--shadow-neo)' }}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={"cell-" + index} fill={entry.count > 0 ? 'var(--color-primary)' : 'rgba(255,255,255,0.1)'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function renderWidget(widget: Widget, props: WidgetProps) {
  switch (widget.type) {
    case 'vitals': return <VitalsWidget {...props} />;
    case 'queue': return <QueueWidget {...props} />;
    case 'category': return <CategoryWidget {...props} />;
    case 'sla': return <SlaWidget {...props} />;
    case 'escalation': return <EscalationWidget {...props} />;
    case 'changeCalendar': return <ChangeCalendarWidget {...props} />;
    case 'severityMatrix': return <SeverityMatrixWidget {...props} />;
    case 'mttr': return <MttrWidget {...props} />;
    case 'threats': return <ThreatsWidget {...props} />;
    case 'sprintBoard': return <StateBoard queue={props.queue} cols={['New', 'Triage', 'Progress', 'Hold']} />;
    case 'reviewPipeline': return <StateBoard queue={props.queue} cols={['New', 'Progress', 'Approval', 'Resolved']} />;
    case 'intake': return <CategoryWidget {...props} />;
    case 'approvals': {
      const items = props.queue.filter((t) => /approval|cab/i.test(t.state));
      return items.length ? (
        <ul className="space-y-2 text-sm">
          {items.map((t) => (
            <li key={t.id} className="flex items-center gap-2">
              <span className="flex-1 truncate">{t.subject}</span>
              <span className="font-mono text-[11px] text-muted-foreground">{t.state}</span>
            </li>
          ))}
        </ul>
      ) : <Empty label="No pending approvals" />;
    }
    case 'statusDistribution': return <StatusDistributionWidget {...props} />;
    default: return <Empty />;
  }
}
